import { Router, watchBusy } from "@frui.ts/screens";
import { computed } from "mobx";
import { Modules } from "config";
import LocalizationService from "services/localizationService";
import ConfirmationService from "services/confirmationService";
import EventBus from "services/eventBus";
import NotificationService from "services/notificationService";
import SecurityService from "services/securityService";
import ListViewModel from "../../listViewModel";
import type { PropertyName } from "@frui.ts/helpers";
import { bound } from "@frui.ts/helpers";
import OptionsService from "services/optionsService";
import EnumService from "services/enumService";
import KioskRepository from "repositories/kioskRepository";
import SearchKiosksRequest from "entities/searchKiosksRequest";
import KioskResponse from "entities/kioskResponse";
import Kiosk from "entities/kiosk";

const navigationName = "kiosks";

@Router.registerRoute({ name: Router.Self, route: navigationName })
export default class KiosksViewModel extends ListViewModel<KioskResponse> {
  readonly modelName = "kiosk";
  protected abortController?: AbortController;
  private options: any;

  constructor(
    protected repository: KioskRepository,
    protected localizationService: LocalizationService,
    protected notificationService: NotificationService,
    protected confirmationService: ConfirmationService,
    protected securityService: SecurityService,
    protected optionsService: OptionsService,
    protected eventBus: EventBus,
    protected enumService: EnumService
  ) {
    super();
    this.navigationName = navigationName;
  }

  @computed
  get name() {
    return this.translate("name");
  }

  @computed
  get booleanOptions() {
    return [
      { id: true, label: this.localizationService.translateGeneral("boolean.true") },
      { id: false, label: this.localizationService.translateGeneral("boolean.false") },
    ];
  }

  protected createFilter(): SearchKiosksRequest {
    return new SearchKiosksRequest();
  }

  @bound
  @watchBusy
  public async loadData() {
    this.abortController = new AbortController();

    if (this.filter.name) {
      this.filter.name = this.filter.name.trim();
    }

    const result = await this.repository.getAll(this.filter, this.pagingFilter, this.abortController?.signal);
    this.abortController = undefined;

    const items = result.items.map(item => this.enumService.proxyObjectEnums(item));
    this.setData([
      items,
      {
        limit: result.metadata.limit,
        offset: result.metadata.offset,
        totalItems: result.metadata.total,
      },
    ]);
  }

  @bound
  allowItemDelete(item: KioskResponse) {
    return false;
  }

  @computed
  get kioskOptions() {
    return this.optionsService.entityOptions(Kiosk).map(k => {
      return {
        id: k.label,
        label: k.label,
      };
    });
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.administration, `kiosks.${code}`, size);
  }

  translateFilterAttribute(attribute: PropertyName<SearchKiosksRequest>, size?: number) {
    return this.localizationService.translateAttribute("kiosksSearchRequest", attribute, size);
  }
}
